import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User, 
  UserCredential,
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  updateProfile,
  sendPasswordResetEmail,
  updateEmail as updateUserEmail,
  updatePassword as updateUserPassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, getDoc, collection, query, where, getDocs, writeBatch, deleteDoc } from 'firebase/firestore';
import { auth, db, storage } from '../firebase';

type AuthContextType = {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  updateProfilePicture: (file: File) => Promise<string>;
  updateDisplayName: (displayName: string) => Promise<string>;
  deleteAccount: (password: string) => Promise<void>;
  loading: boolean;
  error: string;
  setError: (error: string) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      setCurrentUser(user);
      setLoading(false);
      
      // If user is logged in and we're on the login/signup page, redirect to home
      if (user && (window.location.pathname === '/login' || window.location.pathname === '/signup')) {
        window.location.href = '/';
      }
    });

    return unsubscribe;
  }, []);

  async function login(email: string, password: string): Promise<void> {
    setError('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      let errorMessage = 'Failed to log in. Please try again.';
      let showSignUpLink = false;
      
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account exists with this email. Would you like to sign up?';
          showSignUpLink = true;
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed login attempts. Please try again in a few minutes.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'The email address is not valid. Please check and try again.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        default:
          errorMessage = 'An error occurred during login. Please try again.';
      }
      
      setError(errorMessage);
      throw { message: errorMessage, showSignUpLink };
    } finally {
      setLoading(false);
    }
  }

  async function signup(email: string, password: string, displayName: string) {
    try {
      setError('');
      setLoading(true);
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName });
        
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          displayName,
          email,
          photoURL: '',
          createdAt: new Date().toISOString()
        });
      }
    } catch (err) {
      setError('Failed to create account: ' + (err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try {
      setError('');
      await signOut(auth);
    } catch (err) {
      setError('Failed to log out: ' + (err as Error).message);
      throw err;
    }
  }

  async function resetPassword(email: string) {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError('Failed to reset password: ' + (err as Error).message);
      throw err;
    }
  }

  async function updateEmail(email: string) {
    if (!auth.currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    try {
      setError('');
      await updateUserEmail(auth.currentUser, email);
    } catch (err) {
      setError('Failed to update email: ' + (err as Error).message);
      throw err;
    }
  }

  async function updatePassword(password: string) {
    if (!auth.currentUser) {
      throw new Error('No user is currently logged in');
    }

    try {
      setError('');
      await updateUserPassword(auth.currentUser, password);
    } catch (err) {
      setError('Failed to update password: ' + (err as Error).message);
      throw err;
    }
  }

  async function updateProfilePicture(file: File) {
    if (!auth.currentUser) {
      throw new Error('No user is currently logged in');
    }

    try {
      setError('');
      setLoading(true);
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size should be less than 5MB');
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Only image files are allowed');
      }

      // Create a reference to the file in Firebase Storage
      const fileExt = file.name.split('.').pop();
      const fileRef = ref(storage, `profilePictures/${auth.currentUser.uid}.${fileExt}`);
      
      // Upload the file
      await uploadBytes(fileRef, file);
      
      // Get the download URL
      const photoURL = await getDownloadURL(fileRef);
      
      // Update the user's profile
      await updateProfile(auth.currentUser, { photoURL });
      
      // Update the current user in state to reflect the change immediately
      setCurrentUser({
        ...auth.currentUser,
        photoURL
      });
      
      // Update the user document in Firestore
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { photoURL }, { merge: true });
      
      return 'Profile picture updated successfully';
      
    } catch (err) {
      console.error('Error updating profile picture:', err);
      setError('Failed to update profile picture: ' + (err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function updateDisplayName(displayName: string) {
    if (!auth.currentUser) {
      throw new Error('No user is currently logged in');
    }

    try {
      setError('');
      
      // First update the auth profile
      await updateProfile(auth.currentUser, { displayName });
      
      // Update the current user in state to reflect the change immediately
      const updatedUser = {
        ...auth.currentUser,
        displayName
      };
      setCurrentUser(updatedUser);
      
      // Then update the user document in Firestore
      // This is done after state update since it's less critical
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await setDoc(userRef, { displayName }, { merge: true });
      } catch (dbError) {
        console.warn('Firestore update warning:', dbError);
        // Don't fail the whole operation if Firestore update fails
        // The auth profile update is more important
      }
      
      return 'Display name updated successfully';
      
    } catch (err) {
      console.error('Error updating display name:', err);
      setError('Failed to update display name. Please try again.');
      throw err; // Re-throw to allow the component to handle the error
    }
  }

  const deleteAccount = async (password: string) => {
    if (!auth.currentUser) {
      throw new Error('No user is currently logged in');
    }

    try {
      setLoading(true);
      setError('');
      
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        password
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Delete user document from Firestore if it exists
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await deleteDoc(userRef).catch(error => {
        console.warn('Error deleting user document:', error);
        // Continue even if document deletion fails
      });

      // Delete all user's jobs
      const jobsQuery = query(
        collection(db, 'jobs'),
        where('userId', '==', auth.currentUser.uid)
      );
      
      const querySnapshot = await getDocs(jobsQuery);
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // Delete auth account
      await deleteUser(auth.currentUser);
      
      // Sign out the user
      await logout();
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account. Please check your password and try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    updateProfilePicture,
    updateDisplayName,
    deleteAccount,
    loading,
    error,
    setError: setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}